import { useCallback, useMemo, useState } from "react";
import type {
  TimesheetMaterialRecord,
  TimesheetProjectPreset,
  TimesheetRecord,
  TimesheetRowRecord,
} from "@/features/timesheetFlow/timesheetFlowTypes";
import {
  recalcPeriodFromRows,
  recalcTravelEighthsFromRows,
} from "@/features/timesheetFlow/lib/heroTimesImport";

function newRowId(): string {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useTimesheetEditorState(initial?: {
  timesheet?: TimesheetRecord | null;
  rows?: TimesheetRowRecord[];
  materials?: TimesheetMaterialRecord[];
  project?: TimesheetProjectPreset | null;
}) {
  const [timesheet, setTimesheet] = useState<TimesheetRecord | null>(initial?.timesheet ?? null);
  const [project, setProject] = useState<TimesheetProjectPreset | null>(initial?.project ?? null);
  const [rows, setRows] = useState<TimesheetRowRecord[]>(initial?.rows ?? []);
  const [materials, setMaterials] = useState<TimesheetMaterialRecord[]>(initial?.materials ?? []);
  const [workDescription, setWorkDescription] = useState(initial?.timesheet?.work_description ?? "");
  const [materialNote, setMaterialNote] = useState(initial?.timesheet?.material_note ?? "");
  const [travelKm, setTravelKm] = useState<string>(
    initial?.timesheet?.travel_km != null ? String(initial.timesheet.travel_km) : "",
  );
  const [travelEighths, setTravelEighths] = useState<string>(
    initial?.timesheet?.travel_eighths != null ? String(initial.timesheet.travel_eighths) : "",
  );
  const [travelEighthsOverridden, setTravelEighthsOverridden] = useState(
    Boolean(initial?.timesheet?.travel_eighths_overridden),
  );
  const [dateFrom, setDateFrom] = useState(initial?.timesheet?.date_from ?? "");
  const [dateTo, setDateTo] = useState(initial?.timesheet?.date_to ?? "");
  const [heroLocallyFreed, setHeroLocallyFreed] = useState<Set<string>>(new Set());
  const [heroEditableInThisTimesheet, setHeroEditableInThisTimesheet] = useState<Set<string>>(new Set());
  const [subcontractorLocallyFreed, setSubcontractorLocallyFreed] = useState<Set<number>>(new Set());
  const [subcontractorEditableInThisTimesheet, setSubcontractorEditableInThisTimesheet] = useState<Set<number>>(new Set());
  const [employeeTimeLocallyFreed, setEmployeeTimeLocallyFreed] = useState<Set<number>>(new Set());
  const [employeeTimeEditableInThisTimesheet, setEmployeeTimeEditableInThisTimesheet] = useState<Set<number>>(new Set());

  const period = useMemo(() => recalcPeriodFromRows(rows), [rows]);

  const syncPeriodFromRows = useCallback(() => {
    const p = recalcPeriodFromRows(rows);
    if (p) {
      setDateFrom(p.dateFrom);
      setDateTo(p.dateTo);
    }
  }, [rows]);

  const addRow = useCallback((row?: Partial<TimesheetRowRecord>) => {
    setRows((prev) => [
      ...prev,
      {
        work_date: row?.work_date ?? dateFrom ?? "",
        person_name: row?.person_name ?? "",
        hours_decimal: row?.hours_decimal ?? 0,
        time_from: row?.time_from ?? null,
        time_to: row?.time_to ?? null,
        comment: row?.comment ?? null,
        hero_uuid: row?.hero_uuid ?? null,
        subcontractor_time_id: row?.subcontractor_time_id ?? null,
        employee_time_id: row?.employee_time_id ?? null,
        person_login: row?.person_login ?? null,
        hero_row_key: row?.hero_row_key,
        hero_uuids: row?.hero_uuids,
        hero_used_seconds_by_uuid: row?.hero_used_seconds_by_uuid,
        id: undefined,
        ...row,
      },
    ]);
  }, [dateFrom]);

  const updateRow = useCallback((index: number, patch: Partial<TimesheetRowRecord>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }, []);

  const removeRow = useCallback((index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const appendRows = useCallback((newRows: TimesheetRowRecord[]) => {
    if (!newRows.length) return;
    setRows((prev) => [...prev, ...newRows]);
  }, []);

  const addMaterial = useCallback((mat?: Partial<TimesheetMaterialRecord>) => {
    setMaterials((prev) => [
      ...prev,
      {
        menge: mat?.menge ?? 1,
        einheit: mat?.einheit ?? "Stk",
        text: mat?.text ?? "",
        source_type: mat?.source_type ?? "manual",
        ...mat,
      },
    ]);
  }, []);

  const updateMaterial = useCallback((index: number, patch: Partial<TimesheetMaterialRecord>) => {
    setMaterials((prev) => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  }, []);

  const removeMaterial = useCallback((index: number) => {
    setMaterials((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const appendMaterials = useCallback((items: TimesheetMaterialRecord[]) => {
    if (!items.length) return;
    setMaterials((prev) => [...prev, ...items]);
  }, []);

  const applyTravelEighthsFromRows = useCallback(() => {
    if (travelEighthsOverridden) return;
    setTravelEighths(String(recalcTravelEighthsFromRows(rows)));
  }, [rows, travelEighthsOverridden]);

  return {
    timesheet,
    setTimesheet,
    project,
    setProject,
    rows,
    setRows,
    materials,
    setMaterials,
    workDescription,
    setWorkDescription,
    materialNote,
    setMaterialNote,
    travelKm,
    setTravelKm,
    travelEighths,
    setTravelEighths,
    travelEighthsOverridden,
    setTravelEighthsOverridden,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    period,
    syncPeriodFromRows,
    heroLocallyFreed,
    setHeroLocallyFreed,
    heroEditableInThisTimesheet,
    setHeroEditableInThisTimesheet,
    subcontractorLocallyFreed,
    setSubcontractorLocallyFreed,
    subcontractorEditableInThisTimesheet,
    setSubcontractorEditableInThisTimesheet,
    employeeTimeLocallyFreed,
    setEmployeeTimeLocallyFreed,
    employeeTimeEditableInThisTimesheet,
    setEmployeeTimeEditableInThisTimesheet,
    addRow,
    updateRow,
    removeRow,
    appendRows,
    addMaterial,
    updateMaterial,
    removeMaterial,
    appendMaterials,
    applyTravelEighthsFromRows,
    newRowId,
  };
}
